import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { EntityFullDetails } from '@/client/types/entity-full';
import { withErrorHandling } from '@/client/utils/errors';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

type ListingPayload = {
  entities: EntityFullDetails[]
  page: number,
  per_page: number,
  total_entities: number,
  total_pages: number
};

type ApiResponse = {
  cached?: boolean;
  exec_time?: number;
  trace_id?: string;
  status?: number;
  data?: ListingPayload;
} & Partial<ListingPayload>;

type QueryResponse = ListingPayload;

/**
 * This endpoint returns the paginated payload at the top level, while most
 * others nest it under `data`. Accept either so the list renders in both cases.
 */
const unwrap = (response: ApiResponse): QueryResponse => {
  const payload = (response?.data ?? response) as Partial<ListingPayload> & {
    results?: EntityFullDetails[];
    total?: number;
  };

  const entities = payload?.entities ?? payload?.results ?? [];
  const total_entities = payload?.total_entities ?? payload?.total ?? 0;

  return {
    entities,
    page: payload?.page ?? 1,
    per_page: payload?.per_page ?? 0,
    total_entities,
    total_pages: payload?.total_pages ?? 0,
  };
};

const useGetQueryFn = () => {
  const request = useClientStore(state => state.request)

  const queryFn = withErrorHandling(
    async (page: number = 1, additional = {}): Promise<QueryResponse> => {
      const rawParams = { page, ...additional };

      const params: Record<string, string> = Object.entries(rawParams).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>);
  
      const searchParams = new URLSearchParams(params);
      const url = `/api/admin/entities?${searchParams.toString()}`;
  
      const response = await request<ApiResponse>(url);
      return unwrap(response);
    },
    'An unexpected error occurred while fetching listings',
  );

  return queryFn;
}

export const createListingKey = ( page: number, additional: any[] = []) => [
  'listings',
  page,
  ...additional,
];

export const useListingQuery = ( page = 1, additionalProps = {}) => {
  const queryFn = useGetQueryFn();

  return useQuery({
    queryKey: createListingKey( page, [additionalProps]),
    queryFn: () => queryFn(page, additionalProps),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    // Never retry auth errors: a 401/403 here is a per-endpoint permission
    // denial, not a dead session. Retrying stacks 401s and trips the client
    // store's "3 strikes → force logout", bouncing admins to sign-in.
    retry: (failureCount, error: any) =>
      error?.status === 401 || error?.status === 403 ? false : failureCount < 1,
  });
};